import React, { useState } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const Reports: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [reports] = useState([
    {
      id: 1,
      title: '2024년 11월 월간 보고서',
      type: 'monthly',
      date: '2024-11-30',
      status: 'completed',
      profit: '+8.5%',
      trades: 142
    },
    {
      id: 2,
      title: '2024년 3분기 분기 보고서',
      type: 'quarterly',
      date: '2024-09-30',
      status: 'completed',
      profit: '+15.2%',
      trades: 523
    },
    {
      id: 3,
      title: '2024년 10월 월간 보고서',
      type: 'monthly',
      date: '2024-10-31',
      status: 'completed',
      profit: '+6.3%',
      trades: 98
    },
    {
      id: 4,
      title: '그리드 전략 성과 분석',
      type: 'strategy',
      date: '2024-11-15',
      status: 'completed',
      profit: '+12.8%',
      trades: 234
    }
  ]);

  const reportSummary = {
    totalProfit: '+45.3%',
    avgMonthlyReturn: '+3.77%',
    totalTrades: 1847,
    winRate: '62.4%',
    profitFactor: 1.68,
    sharpeRatio: 1.92
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case 'monthly': return '📅';
      case 'quarterly': return '📊';
      case 'strategy': return '🎯';
      default: return '📄';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">보고서</h2>
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
          <DocumentChartBarIcon className="h-5 w-5 mr-2" />
          새 보고서 생성
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* 보고서 목록 */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 border border-light-300 dark:border-dark-600">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">최근 보고서</h3>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-1 text-sm"
              >
                <option value="all">전체</option>
                <option value="month">월간</option>
                <option value="quarter">분기</option>
                <option value="year">연간</option>
              </select>
            </div>

            <div className="space-y-3">
              {reports.map(report => (
                <div
                  key={report.id}
                  className="bg-light-100 dark:bg-dark-700 border border-light-300 dark:border-dark-600 rounded-lg p-4 hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <span className="text-2xl mr-3">{getReportIcon(report.type)}</span>
                      <div>
                        <h4 className="text-gray-900 dark:text-white font-medium">{report.title}</h4>
                        <div className="flex items-center space-x-4 mt-2 text-sm">
                          <span className="text-gray-600 dark:text-gray-400 flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {report.date}
                          </span>
                          <span className={`font-medium ${report.profit.startsWith('+') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            수익률: {report.profit}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            거래: {report.trades}건
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="bg-light-200 dark:bg-dark-600 hover:bg-light-300 dark:hover:bg-dark-500 text-gray-900 dark:text-white p-2 rounded">
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="w-full mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium">
              더 많은 보고서 보기
            </button>
          </div>

          {/* 보고서 생성 도구 */}
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 border border-light-300 dark:border-dark-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">맞춤 보고서 생성</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">보고서 유형</label>
                <select className="w-full mt-1 bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2">
                  <option>성과 보고서</option>
                  <option>거래 분석</option>
                  <option>위험 분석</option>
                  <option>전략 비교</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">기간</label>
                <select className="w-full mt-1 bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2">
                  <option>지난 7일</option>
                  <option>지난 30일</option>
                  <option>지난 분기</option>
                  <option>맞춤 기간</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">포함할 데이터</label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center">
                    <input type="checkbox" defaultChecked className="mr-2" />
                    <span className="text-sm text-gray-900 dark:text-white">거래 내역</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" defaultChecked className="mr-2" />
                    <span className="text-sm text-gray-900 dark:text-white">수익률 차트</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm text-gray-900 dark:text-white">위험 지표</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">파일 형식</label>
                <select className="w-full mt-1 bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 text-gray-900 dark:text-white rounded px-3 py-2">
                  <option>PDF</option>
                  <option>Excel</option>
                  <option>CSV</option>
                </select>
              </div>
            </div>
            
            <button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium">
              보고서 생성
            </button>
          </div>
        </div>

        {/* 요약 통계 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 border border-light-300 dark:border-dark-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">전체 성과 요약</h3>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">{reportSummary.totalProfit}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">총 수익률</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportSummary.avgMonthlyReturn}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">월평균수익률</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportSummary.totalTrades}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">총 거래 횟수</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportSummary.winRate}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">승률</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportSummary.profitFactor}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">수익 팩터</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportSummary.sharpeRatio}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">샤프 비율</div>
              </div>
            </div>
          </div>

          {/* 빠른 다운로드 */}
          <div className="bg-white dark:bg-dark-800 rounded-lg p-6 border border-light-300 dark:border-dark-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">빠른 다운로드</h3>
            
            <div className="space-y-3">
              <button className="w-full bg-light-200 dark:bg-dark-700 hover:bg-light-300 dark:hover:bg-dark-600 text-gray-900 dark:text-white py-2 px-4 rounded flex items-center justify-between">
                <span>이번 달 보고서</span>
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              
              <button className="w-full bg-light-200 dark:bg-dark-700 hover:bg-light-300 dark:hover:bg-dark-600 text-gray-900 dark:text-white py-2 px-4 rounded flex items-center justify-between">
                <span>분기별 보고서</span>
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              
              <button className="w-full bg-light-200 dark:bg-dark-700 hover:bg-light-300 dark:hover:bg-dark-600 text-gray-900 dark:text-white py-2 px-4 rounded flex items-center justify-between">
                <span>연간 보고서</span>
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports; 